import type { IssueTreeNode } from '../../../services/issueService';

// --- Types ---

export type TimeUnit = 'day' | 'week' | 'month';

export interface FlatNode extends Omit<IssueTreeNode, 'children'> {
  depth: number;
  hasChildren: boolean;
  children: IssueTreeNode[];
}

export interface TimelineFilterState {
  types: string[];
  assignees: string[];
  statuses: string[];
}

export interface TimeRange {
  minDate: Date;
  maxDate: Date;
}

// --- Constants ---

export const CELL_WIDTH: Record<TimeUnit, number> = {
  day: 40,
  week: 120,
  month: 200,
};

export const ROW_HEIGHT = 36;

/** 캘린더 헤더 높이 — day 모드는 2-row(날짜+요일)라 더 큼.
 * IssueTree 헤더(작업 컬럼)도 같은 값을 사용해야 좌우 줄이 맞음. */
export function getHeaderHeight(timeUnit: TimeUnit): number {
  return timeUnit === 'day' ? 42 : ROW_HEIGHT;
}

export const EMPTY_FILTERS: TimelineFilterState = {
  types: [],
  assignees: [],
  statuses: [],
};

// --- Tree Flatten ---

function passesFilter(node: IssueTreeNode, filters: TimelineFilterState): boolean {
  if (filters.types.length > 0 && !filters.types.includes(node.issueType)) return false;
  if (filters.statuses.length > 0 && !filters.statuses.includes(node.workflowStatus.name)) return false;
  if (filters.assignees.length > 0) {
    const assigneeId = node.assignee?.id ?? '';
    if (!filters.assignees.includes(assigneeId)) return false;
  }
  return true;
}

function hasVisibleDescendant(node: IssueTreeNode, filters: TimelineFilterState): boolean {
  if (passesFilter(node, filters)) return true;
  return node.children?.some((c: IssueTreeNode) => hasVisibleDescendant(c, filters)) ?? false;
}

export function flattenTree(
  nodes: IssueTreeNode[],
  expandedIds: Set<string>,
  filters: TimelineFilterState,
  depth = 0,
): FlatNode[] {
  const hasActiveFilter =
    filters.types.length > 0 || filters.assignees.length > 0 || filters.statuses.length > 0;
  const result: FlatNode[] = [];

  for (const node of nodes) {
    if (hasActiveFilter && !hasVisibleDescendant(node, filters)) continue;

    result.push({
      issueId: node.issueId,
      title: node.title,
      issueType: node.issueType,
      statusId: node.statusId,
      workflowStatus: node.workflowStatus,
      priority: node.priority,
      startDate: node.startDate,
      dueDate: node.dueDate,
      parentIssueId: node.parentIssueId,
      assignee: node.assignee,
      depth,
      hasChildren: (node.children?.length ?? 0) > 0,
      children: node.children ?? [],
    });

    if (expandedIds.has(node.issueId) && node.children?.length) {
      result.push(...flattenTree(node.children, expandedIds, filters, depth + 1));
    }
  }
  return result;
}

// --- Date helpers (native JS, no external libs) ---

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function diffDays(a: Date, b: Date): number {
  const msPerDay = 86_400_000;
  return Math.round((a.getTime() - b.getTime()) / msPerDay);
}

export function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

export function startOfWeek(d: Date): Date {
  const r = startOfDay(d);
  const day = r.getDay(); // 0=Sun
  r.setDate(r.getDate() - ((day + 6) % 7)); // Mon-based
  return r;
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export function getWeekNumber(d: Date): number {
  const oneJan = new Date(d.getFullYear(), 0, 1);
  const days = Math.floor((d.getTime() - oneJan.getTime()) / 86_400_000);
  return Math.ceil((days + oneJan.getDay() + 1) / 7);
}

export function formatDateShort(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

// --- Time range & columns ---

export interface TimeColumn {
  date: Date;
  label: string;
  subLabel?: string;
  isToday: boolean;
}

export function calculateTimeRange(nodes: FlatNode[], timeUnit: TimeUnit): TimeRange {
  const today = startOfDay(new Date());
  let minDate = new Date(today);
  let maxDate = new Date(today);

  for (const node of nodes) {
    if (node.startDate) {
      const d = startOfDay(new Date(node.startDate));
      if (d < minDate) minDate = d;
    }
    if (node.dueDate) {
      const d = startOfDay(new Date(node.dueDate));
      if (d > maxDate) maxDate = d;
    }
  }

  // Add padding
  const padDays = timeUnit === 'day' ? 14 : timeUnit === 'week' ? 21 : 60;
  minDate = addDays(minDate, -padDays);
  maxDate = addDays(maxDate, padDays);

  // Align to unit boundary
  if (timeUnit === 'week') {
    minDate = startOfWeek(minDate);
  } else if (timeUnit === 'month') {
    minDate = startOfMonth(minDate);
  }

  return { minDate, maxDate };
}

export function generateColumns(range: TimeRange, timeUnit: TimeUnit): TimeColumn[] {
  const cols: TimeColumn[] = [];
  const today = startOfDay(new Date());
  let current = new Date(range.minDate);

  while (current <= range.maxDate) {
    const isToday =
      timeUnit === 'day'
        ? current.getTime() === today.getTime()
        : timeUnit === 'week'
          ? current.getTime() <= today.getTime() && addDays(current, 7).getTime() > today.getTime()
          : current.getMonth() === today.getMonth() &&
            current.getFullYear() === today.getFullYear();

    if (timeUnit === 'day') {
      cols.push({
        date: new Date(current),
        label: formatDateShort(current),
        isToday,
      });
      current = addDays(current, 1);
    } else if (timeUnit === 'week') {
      const weekEnd = addDays(current, 6);
      cols.push({
        date: new Date(current),
        label: `W${getWeekNumber(current)}`,
        subLabel: `${formatDateShort(current)}~${formatDateShort(weekEnd)}`,
        isToday,
      });
      current = addDays(current, 7);
    } else {
      cols.push({
        date: new Date(current),
        label: `${current.getMonth() + 1}월`,
        subLabel: `${current.getFullYear()}`,
        isToday,
      });
      current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    }
  }
  return cols;
}

// --- Bar position ---

export function getDaysPerUnit(timeUnit: TimeUnit): number {
  if (timeUnit === 'day') return 1;
  if (timeUnit === 'week') return 7;
  return 30; // approx
}

export function getPixelsPerDay(timeUnit: TimeUnit): number {
  return CELL_WIDTH[timeUnit] / getDaysPerUnit(timeUnit);
}

export function formatDateISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// --- Client-side hierarchy validation (mirrors server issueHierarchy.ts) ---

const ALLOWED_PARENT_TYPES: Record<string, string[] | null> = {
  EPIC: null,
  STORY: ['EPIC'],
  TASK: ['STORY'],
  SUB_TASK: ['TASK'],
  BUG: ['EPIC', 'STORY', 'TASK', 'SUB_TASK'],
};

export function canMoveToParent(issueType: string, parentType: string | null): boolean {
  if (issueType === 'EPIC') return false; // EPIC can never be moved under another issue
  if (!parentType) {
    // Moving to top-level: only BUG and EPIC allowed
    return issueType === 'BUG' || issueType === 'EPIC';
  }
  const allowed = ALLOWED_PARENT_TYPES[issueType];
  if (!allowed) return false;
  return allowed.includes(parentType);
}

export function calculateBarPosition(
  startDate: string | null,
  dueDate: string | null,
  range: TimeRange,
  timeUnit: TimeUnit,
): { left: number; width: number } | null {
  if (!startDate && !dueDate) return null;

  const cellWidth = CELL_WIDTH[timeUnit];
  const pxPerDay = cellWidth / getDaysPerUnit(timeUnit);

  const sd = startDate ? startOfDay(new Date(startDate)) : null;
  const dd = dueDate ? startOfDay(new Date(dueDate)) : null;

  if (sd && dd) {
    const left = diffDays(sd, range.minDate) * pxPerDay;
    const days = diffDays(dd, sd) + 1; // inclusive
    const width = Math.max(days * pxPerDay, cellWidth * 0.5);
    return { left, width };
  }

  if (sd) {
    const left = diffDays(sd, range.minDate) * pxPerDay;
    return { left, width: pxPerDay };
  }

  if (dd) {
    const left = diffDays(dd, range.minDate) * pxPerDay;
    return { left, width: pxPerDay };
  }

  return null;
}

export function getTodayOffset(range: TimeRange, timeUnit: TimeUnit): number {
  const today = startOfDay(new Date());
  const pxPerDay = CELL_WIDTH[timeUnit] / getDaysPerUnit(timeUnit);
  return diffDays(today, range.minDate) * pxPerDay;
}

export function getTotalWidth(columns: TimeColumn[], timeUnit: TimeUnit): number {
  return columns.length * CELL_WIDTH[timeUnit];
}
